// C:\Projects\texe-web\src\components\NotificationsBell.tsx
import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import { apiGet, apiPost, openRealtimeStream } from "../lib/api";
import type { NotificationItem as NItem } from "../lib/api";
import Avatar from "./Avatar";
import { useAuth } from "../context/AuthProvider";

// Derin link: Yoruma gitmek için ?c=<commentId>
const buildPostHref = (postId: string, commentId?: string) =>
  commentId ? `/p/${postId}?c=${commentId}` : `/p/${postId}`;

export default function NotificationsBell() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState<number>(0);
  const [items, setItems] = useState<NItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const itemRefs = useRef<Array<HTMLDivElement | null>>([]);
  const [focusIdx, setFocusIdx] = useState<number>(-1);
  const [panelPosition, setPanelPosition] = useState<{ top: number; right: number } | null>(null);

  const panelId = "notifications-popover";
  const navigate = useNavigate();

  // SSE bağlantısı
  const sseRef = useRef<ReturnType<typeof openRealtimeStream> | null>(null);

  // 🔁 open state'ini güncel okumak için ref
  const openRef = useRef(open);
  useEffect(() => {
    openRef.current = open;
  }, [open]);

  const refreshCount = useCallback(async () => {
    try {
      const res = await apiGet<{ ok: true; count: number; unread?: number }>(
        "/api/notifications/count"
      );
      setCount((res.count ?? res.unread ?? 0) || 0);
    } catch {
      // sessiz geç
    }
  }, []);

  const loadLatest = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiGet<{ ok: true; items: NItem[] }>(
        "/api/notifications?limit=10"
      );
      setItems(res.items);
      setFocusIdx(res.items.length > 0 ? 0 : -1);
    } catch {
      setError("Bildirimler alınamadı.");
      setItems([]);
      setFocusIdx(-1);
    } finally {
      setLoading(false);
    }
  }, []);

  // ↪️ SSE handler içinde en güncel loadLatest'e ulaşmak için ref
  const loadFnRef = useRef(loadLatest);
  useEffect(() => {
    loadFnRef.current = loadLatest;
  }, [loadLatest]);

  useEffect(() => {
    // Giriş yapmamışsa count API çağrısı yapma
    if (!user) return;
    refreshCount();
  }, [refreshCount, user]);

  useEffect(() => {
    if (open) {
      void loadLatest();
      setTimeout(() => {
        if (listRef.current) (listRef.current as HTMLElement).focus();
      }, 0);
      
      // Button pozisyonunu hesapla
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        const windowWidth = window.innerWidth;
        const panelWidth = 380;
        
        // Dar ekranda (mobil) panel'i ortala
        if (windowWidth < 600) {
          setPanelPosition({
            top: rect.bottom + 8,
            right: (windowWidth - Math.min(panelWidth, windowWidth - 32)) / 2,
          });
        } else {
          // Geniş ekranda sağa hizala
          setPanelPosition({
            top: rect.bottom + 8,
            right: windowWidth - rect.right,
          });
        }
      }
    }
  }, [open, loadLatest]);

  // dış tıklama ile kapat
  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (!panelRef.current) return;
      if (!panelRef.current.contains(e.target as Node)) setOpen(false);
    }
    if (open) {
      document.addEventListener("mousedown", onClick);
      return () => document.removeEventListener("mousedown", onClick);
    }
  }, [open]);

  // ESC + ok/enter
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (!openRef.current) return;
      if (e.key === "Escape") {
        e.preventDefault();
        setOpen(false);
        return;
      }
      if (!items.length) return;

      if (e.key === "ArrowDown") {
        e.preventDefault();
        setFocusIdx((v) => {
          const next = Math.min(items.length - 1, v + 1);
          itemRefs.current[next]?.focus?.();
          return next;
        });
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setFocusIdx((v) => {
          const next = Math.max(0, v - 1);
          itemRefs.current[next]?.focus?.();
          return next;
        });
      } else if (e.key === "Enter") {
        e.preventDefault();
        const idx = Math.max(0, focusIdx);
        const n = items[idx];
        if (n) {
          const href = primaryHref(n);
          if (href) {
            navigate(href);
            setOpen(false);
          }
        }
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, items, focusIdx, navigate]);

  const markOneRead = useCallback(async (id: string) => {
    try {
      await apiPost(`/api/notifications/${id}/read`, {});
      setItems((curr) => curr.map((n) => (n.id === id ? { ...n, read: true } : n)));
      setCount((c) => (c > 0 ? c - 1 : 0));
    } catch (err) {
      console.warn("Failed to mark notification as read:", err);
    }
  }, []);

  const markAllRead = useCallback(async () => {
    try {
      await apiPost(`/api/notifications/read-all`, {});
      setItems((curr) => curr.map((n) => ({ ...n, read: true })));
      setCount(0);
    } catch (err) {
      console.warn("Failed to mark all notifications as read:", err);
    }
  }, []);

  const primaryHref = useCallback((n: NItem): string | null => {
    // Announcement'lar için link yok
    if (n.type === "announcement") return null;
    
    // SavWar başladı bildirimi için /savwar'a yönlendir
    if (n.type === "savwar_start") return "/savwar";
    
    // SavWar sonuçları için /savwar'a yönlendir
    if (n.type === "savwar_result") return "/savwar";
    
    // SavLike winner için profil sayfasına git
    if (n.type === "savlike_winner") return null;
    
    // Kabile davet bildirimi - butonlarla yönetiliyor
    if (n.type === "kabile_invite") return null;
    
    // Kabile katılma talebi - kabile yönetim sayfasına
    if (n.type === "kabile_join_request") return "/savkabile/my";
    
    const postId = n.comment?.postId ?? n.post?.id ?? null;
    const commentId = n.comment?.id ?? undefined;
    if (postId) return buildPostHref(postId, commentId);
    if (n.actor?.username) return `/u/${encodeURIComponent(n.actor.username)}`;
    if (n.actor?.id) return `/u/${encodeURIComponent(n.actor.id)}`;
    return null;
  }, []);

  function renderText(n: NItem) {
    const actor =
      n.actor?.username ? <Link to={`/u/${n.actor.username}`}>@{n.actor.username}</Link> : <>Biri</>;
    
    // Announcement ve SavWar Start için özel gösterim
    if (n.type === "announcement") {
      return (
        <div style={{ 
          backgroundColor: 'rgba(17, 66, 50, 0.2)', 
          padding: 12, 
          borderRadius: 8, 
          borderLeft: '3px solid #114232',
          marginTop: 6
        }}>
          <div style={{ fontWeight: 'bold', color: '#fff', marginBottom: 4 }}>📢 Sistem Duyurusu</div>
          <div style={{ color: '#fff', lineHeight: 1.5 }}>{n.text || 'Yeni bir duyuru var!'}</div>
        </div>
      );
    }
    
    if (n.type === "savwar_start") {
      return (
        <div style={{ 
          backgroundColor: 'rgba(255, 215, 0, 0.15)', 
          padding: 12, 
          borderRadius: 8, 
          borderLeft: '3px solid #FFD700',
          marginTop: 6
        }}>
          <div style={{ fontWeight: 'bold', color: '#FFD700', marginBottom: 4 }}>⚔️ SavWar Başladı!</div>
          <div style={{ color: '#fff', lineHeight: 1.5 }}>{n.text || 'Savaş alanına katılın!'}</div>
        </div>
      );
    }
    
    if (n.type === "savwar_result") {
      return (
        <div style={{ 
          backgroundColor: 'rgba(17, 66, 50, 0.2)', 
          padding: 12, 
          borderRadius: 8, 
          borderLeft: '3px solid #114232',
          marginTop: 6
        }}>
          <div style={{ fontWeight: 'bold', color: '#114232', marginBottom: 4 }}>📊 SavWar Sonuçları</div>
          <div style={{ color: '#fff', lineHeight: 1.5 }}>{n.text || 'Sıralama açıklandı!'}</div>
        </div>
      );
    }
    
    if (n.type === "savlike_winner") {
      return (
        <div style={{ 
          backgroundColor: 'rgba(255, 215, 0, 0.15)', 
          padding: 12, 
          borderRadius: 8, 
          borderLeft: '3px solid #FFD700',
          marginTop: 6
        }}>
          <div style={{ fontWeight: 'bold', color: '#FFD700', marginBottom: 4 }}>🏆 Tebrikler!</div>
          <div style={{ color: '#fff', lineHeight: 1.5 }}>{n.text || 'Sıralamada üst sıralara çıktınız!'}</div>
        </div>
      );
    }
    
    if (n.type === "follow") return <>{actor} seni <b>takip etmeye başladı</b>.</>;
    if (n.type === "like")
      return n.post ? (
        <>
          {actor} <b>gönderini beğendi</b>{" — "}
          <Link to={buildPostHref(n.post.id)}>Gör</Link>.
        </>
      ) : (
        <>
          {actor} <b>beğendi</b>.
        </>
      );
    if (n.type === "comment") {
      const postId = n.comment?.postId ?? n.post?.id ?? null;
      const commentId = n.comment?.id ?? undefined;
      return (
        <>
          {actor} gönderine <b>yorum yaptı</b>
          {postId && <> — <Link to={buildPostHref(postId, commentId)}>Gör</Link></>}.
        </>
      );
    }
    if (n.type === "comment_like") {
      const postId = n.comment?.postId ?? n.post?.id ?? null;
      const commentId = n.comment?.id ?? undefined;
      return (
        <>
          {actor} <b>yorumunu beğendi</b>
          {postId && <> — <Link to={buildPostHref(postId, commentId)}>Gör</Link></>}.
        </>
      );
    }
    if (n.type === "mention") {
      const postId = n.comment?.postId ?? n.post?.id ?? null;
      const commentId = n.comment?.id ?? undefined;
      return (
        <>
          {actor} senden <b>bahsetti</b>
          {postId && <> — <Link to={buildPostHref(postId, commentId)}>Gör</Link></>}.
        </>
      );
    }
    if (n.type === "kabile_invite") {
      return (
        <>
          {actor} seni <b>"{n.kabile?.name || 'kabile'}"</b> kabilesine davet etti.
        </>
      );
    }
    if (n.type === "kabile_join_request") {
      return (
        <>
          {n.text || `${actor} kabilenize katılmak istiyor.`}
        </>
      );
    }
    return <>Bildirim</>;
  }

  // 🔴 SSE
  useEffect(() => {
    sseRef.current?.close();
    sseRef.current = openRealtimeStream({
      onNotification: (payload: any) => {
        if (payload && typeof payload === "object") {
          if (payload.kind === "unread_count") {
            const val = typeof payload.value === "number" ? payload.value : payload.count;
            if (typeof val === "number") setCount(val);
            return;
          }
          if (payload.kind === "new") {
            setCount((c) => c + 1);
            if (openRef.current) loadFnRef.current();
            return;
          }
          if (payload.kind === "read") {
            const id = String(payload.id ?? "");
            if (id) setItems((curr) => curr.map((n) => (n.id === id ? { ...n, read: true } : n)));
            return;
          }
          if (payload.kind === "read_all") {
            setItems((curr) => curr.map((n) => ({ ...n, read: true })));
            setCount(0);
            return;
          }
          if (
            payload.type === "like" ||
            payload.type === "comment" ||
            payload.type === "follow" ||
            payload.type === "comment_like" ||
            payload.type === "mention" ||
            payload.type === "announcement" ||
            payload.type === "savwar_start" ||
            payload.type === "savwar_result" ||
            payload.type === "savlike_winner"
          ) {
            setCount((c) => c + 1);
            if (openRef.current) loadFnRef.current();
            return;
          }
        }
      },
    });
    return () => {
      sseRef.current?.close();
      sseRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const badge = useMemo(
    () =>
      count > 0 ? (
        <span
          aria-label={`${count} okunmamış bildirim`}
          style={{
            position: "absolute",
            top: -6,
            right: -6,
            background: "crimson",
            color: "#fff",
            borderRadius: 10,
            padding: "0 6px",
            fontSize: 12,
            lineHeight: "18px",
            minWidth: 18,
            textAlign: "center",
          }}
        >
          {count}
        </span>
      ) : null,
    [count]
  );

  return (
    <>
      <button
        ref={buttonRef}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        aria-label="Bildirimleri aç/kapat"
        style={{
          position: "relative",
          border: "1px solid #2c2c2c",
          background: "transparent",
          color: "inherit",
          borderRadius: 8,
          padding: "6px 10px",
          cursor: "pointer",
        }}
        title="Bildirimler"
      >
        🔔
        {badge}
      </button>

      {open && panelPosition && createPortal(
        <>
          {/* Backdrop - tıklanınca kapanır */}
          <div
            onClick={() => setOpen(false)}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0, 0, 0, 0.4)",
              zIndex: 9999,
            }}
          />
          
          <div
            ref={panelRef}
            id={panelId}
            role="dialog"
            aria-label="Bildirimler"
            style={{
              position: "fixed",
              top: panelPosition.top,
              right: panelPosition.right,
              width: window.innerWidth < 600 ? `calc(100vw - 32px)` : 380,
              maxWidth: 380,
              maxHeight: 420,
              overflowY: "auto",
              background: "#0c0c0c",
              border: "1px solid #2c2c2c",
              borderRadius: 10,
              boxShadow: "0 10px 24px rgba(0,0,0,0.5)",
              zIndex: 10000,
            }}
          >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              padding: 10,
              borderBottom: "1px solid #2c2c2c",
            }}
          >
            <strong>Bildirimler</strong>
            <button onClick={markAllRead} style={{ fontSize: 12 }}>
              Tümünü okundu
            </button>
          </div>

          <div
            ref={listRef}
            tabIndex={-1}
            style={{ padding: 8, outline: "none" }}
            aria-activedescendant={
              focusIdx >= 0 && items[focusIdx] ? `notif-row-${items[focusIdx].id}` : undefined
            }
          >
            {loading && <div style={{ padding: 8 }}>Yükleniyor...</div>}
            {error && <div style={{ padding: 8, color: "tomato" }}>{error}</div>}
            {!loading && !error && items.length === 0 && (
              <div style={{ padding: 8, opacity: 0.7 }}>Bildirim yok.</div>
            )}

            {!loading &&
              !error &&
              items.map((n, i) => {
                const href = primaryHref(n);
                const small = n.actor?.avatarUrlSmall || n.actor?.avatarUrl || undefined;

                return (
                  <div
                    id={`notif-row-${n.id}`}
                    key={n.id}
                    ref={(el) => {
                      itemRefs.current[i] = el;
                    }}
                    tabIndex={0}
                    role="button"
                    aria-label="Bildirimi aç"
                    onClick={() => {
                      if (href) navigate(href);
                      setOpen(false);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && href) {
                        e.preventDefault();
                        navigate(href);
                        setOpen(false);
                      }
                    }}
                    style={{
                      border: "1px solid #2c2c2c",
                      borderRadius: 8,
                      padding: 10,
                      marginBottom: 8,
                      background: n.read ? "transparent" : "rgba(0, 120, 255, 0.07)",
                      display: "grid",
                      gridTemplateColumns: "28px 1fr",
                      gap: 10,
                      alignItems: "flex-start",
                      cursor: href ? "pointer" : "default",
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "center", paddingTop: 2 }}>
                      <Avatar src={small} alt={n.actor?.username || "avatar"} size={24} />
                    </div>

                    <div style={{ minWidth: 0 }}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          fontSize: 12,
                          opacity: 0.8,
                        }}
                      >
                        <span>{new Date(n.createdAt).toLocaleString()}</span>
                        {!n.read && (
                          <button
                            style={{ fontSize: 12 }}
                            onClick={(e) => {
                              e.stopPropagation();
                              void markOneRead(n.id);
                            }}
                          >
                            Okundu
                          </button>
                        )}
                      </div>

                      <div style={{ marginTop: 6, fontSize: 14, lineHeight: 1.35 }}>
                        {renderText(n)}
                      </div>

                      {n.type === 'comment' && n.comment?.text && (
                        <div
                          style={{
                            marginTop: 6,
                            fontSize: 13,
                            color: "#ccc",
                            background: "#111",
                            border: "1px solid #222",
                            borderRadius: 6,
                            padding: "6px 8px",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            maxWidth: "100%",
                            fontStyle: "italic",
                            opacity: 0.8,
                          }}
                        >
                          "{n.comment.text.slice(0, 60)}{n.comment.text.length > 60 ? '...' : ''}"
                        </div>
                      )}
                      {n.type === 'like' && n.post?.text && (
                        <div
                          style={{
                            marginTop: 6,
                            fontSize: 13,
                            color: "#ccc",
                            background: "#111",
                            border: "1px solid #222",
                            borderRadius: 6,
                            padding: "6px 8px",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            maxWidth: "100%",
                            fontStyle: "italic",
                            opacity: 0.8,
                          }}
                        >
                          "{n.post.text.slice(0, 60)}{n.post.text.length > 60 ? '...' : ''}"
                        </div>
                      )}
                      {n.type === 'comment_like' && n.comment?.text && (
                        <div
                          style={{
                            marginTop: 6,
                            fontSize: 13,
                            color: "#ccc",
                            background: "#111",
                            border: "1px solid #222",
                            borderRadius: 6,
                            padding: "6px 8px",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            maxWidth: "100%",
                            fontStyle: "italic",
                            opacity: 0.8,
                          }}
                        >
                          "{n.comment.text.slice(0, 60)}{n.comment.text.length > 60 ? '...' : ''}"
                        </div>
                      )}
                      {n.type === 'mention' && (n.comment?.text || n.post?.text) && (
                        <div
                          style={{
                            marginTop: 6,
                            fontSize: 13,
                            color: "#ccc",
                            background: "#111",
                            border: "1px solid #222",
                            borderRadius: 6,
                            padding: "6px 8px",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            maxWidth: "100%",
                            fontStyle: "italic",
                            opacity: 0.8,
                          }}
                        >
                          "{(n.comment?.text || n.post?.text || '').slice(0, 60)}{(n.comment?.text || n.post?.text || '').length > 60 ? '...' : ''}"
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
          </div>

          <div
            style={{
              padding: 8,
              borderTop: "1px solid #2c2c2c",
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <Link to="/notifications" onClick={() => setOpen(false)}>
              Tümünü gör →
            </Link>
          </div>
        </div>
        </>,
        document.body
      )}
    </>
  );
}
